// Changed import syntax to properly import axios
import axios from "axios";

const API_URL = `${process.env.REACT_APP_API_URL}`;

// Set base URL after properly importing axios
axios.defaults.baseURL = API_URL;

const CancelToken = axios.CancelToken;
let source = CancelToken.source();

/* Set common header parameters */
const setDefaultHeaders = (isMultipartData = false) => {
  if (isMultipartData) {
    axios.defaults.headers.common = {
      "Content-Type": "multipart/form-data",
      Accept: "application/json",
    };
  } else {
    axios.defaults.headers.common = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }
};

// Initialize default headers
setDefaultHeaders();

/**
 * Set header authorization
 * @param token Authorization token
 */
export const setHeaderAuthorization = (token) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    axios.defaults.headers["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers["Authorization"];
  }
};

/**
 * Set header content type
 * @param isMultipartData boolean
 */
export const setHeaderContentType = (isMultipartData) => {
  setDefaultHeaders(isMultipartData);
};

/**
 * Cancel all request
 */
export const cancelAllRequest = () => {
  source.cancel();
  setTimeout(() => {
    source = CancelToken.source();
  }, 1500);
};

/**
 * Handle Axios response
 * @param res   HTTP Response
 * @returns     Return data
 */
export const getResponse = (res) => {
  if (res && (res.status === 200 || res.status === 201 || res.status === 204)) {
    return res.data;
  }
  throw new Error(res?.data?.message || "Some error occurred");
};

/**
 * Handle Axios Error response
 * @param res   HTTP Response
 * @returns     Return data
 */
export const getError = (res) => {
  // Check for 401 status and redirect to root path
  if (res.response?.status === 401) {
    window.location.href = "/";
  }
  return res.response?.data || { message: "An error occurred" };
};

/**
 * Get request
 * @param path      API url path
 * @param params    Request parameters
 * @param headers   Request headers
 */
export const get = (path, params, headers = {}, customBaseURL = null) => {
  return new Promise((resolve, reject) => {
    try {
      const requestHeaders = {
        ...axios.defaults.headers.common,
        ...headers,
      };

      axios
        .get(path, {
          params,
          headers: requestHeaders,
          baseURL: customBaseURL || axios.defaults.baseURL,
        })
        .then(getResponse)
        .then(resolve)
        .catch((res) => reject(getError(res)));
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Post request
 * @param path      API url path
 * @param params    Request parameters
 * @param headers   Request headers
 */
export const post = (path, params, headers = {}, customBaseURL = null) => {
  return new Promise((resolve, reject) => {
    try {
      const requestHeaders = {
        ...axios.defaults.headers.common,
        ...headers,
      };

      axios
        .post(path, params || {}, {
          headers: requestHeaders,
          baseURL: customBaseURL || axios.defaults.baseURL,
        })
        .then(getResponse)
        .then(resolve)
        .catch((res) => reject(getError(res)));
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Put request
 * @param path      API url path
 * @param params    Request parameters
 * @param headers   Request headers
 */
export const put = (path, params, headers = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const requestHeaders = {
        ...axios.defaults.headers.common,
        ...headers,
      };

      axios
        .put(path, params || {}, { headers: requestHeaders })
        .then(getResponse)
        .then(resolve)
        .catch((res) => reject(getError(res)));
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Patch request
 * @param path      API url path
 * @param params    Request parameters
 * @param headers   Request headers
 */
export const patch = (path, params, headers = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const requestHeaders = {
        ...axios.defaults.headers.common,
        ...headers,
      };

      axios
        .patch(path, params || {}, { headers: requestHeaders })
        .then(getResponse)
        .then(resolve)
        .catch((res) => reject(getError(res)));
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Delete request
 * @param path      API url path
 * @param params    Request parameters
 * @param headers   Request headers
 */
export const remove = (path, params, headers) => {
  return new Promise((resolve, reject) => {
    try {
      axios
        .delete(path, { data: params, headers })
        .then(getResponse)
        .then(resolve)
        .catch((res) => reject(getError(res)));
    } catch (error) {
      reject(error);
    }
  });
};
